/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';

// Antd
import {
  Row,
  Spin,
  Modal,
  Form,
  Col,
} from 'antd';

// Utils
import { checkToken } from 'api/utils';
import { onError } from 'utils/handlers';
import { formatReceived } from 'utils/formatValues';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import { getModelPermission } from 'utils/permission';

// API
import { getEstadosGlobales } from 'api/catalogos';
import { getConfiguracionDescuentos } from 'api/nominas/descuentos/configuracionDescuentos';
import {
  getDescuentosEmpleadosDescuentos,
  getDescuentoEmpleadoDescuento,
  postDescuentoEmpleadoDescuento,
  patchDescuentoEmpleadoDescuento,
  deleteDescuentoEmpleadoDescuento,
} from 'api/nominas/descuentos/descuentosEmpleadosDescuentos';

// Componentes
import Table from 'components/Table';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import FormSubmitControls from 'components/FormSubmitControls';

export const descuentosEmpleadosDescuentosPermission = {
  permissionModel: 'descuentoempleadodescuento',
  catalogsModels: [],
  tabsModels: [],
};

/* eslint-disable react/prop-types */
const DescuentosEmpleadosDescuentos = ({ descuentoEmpleado, descuentoEmpleadoAutorizadoInactivo, isBlocked }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [open, setVisible] = useState(false);
  const [estados, setEstados] = useState([]);
  const [descuentos, setDescuentos] = useState([]);
  const [descuentosFiltered, setDescuentosFiltered] = useState([]);
  const [disableAdd, setDisableAdd] = useState(false);
  const [form] = Form.useForm();

  const getPermissionNominas = getModelPermission(
    descuentosEmpleadosDescuentosPermission.permissionModel,
    descuentosEmpleadosDescuentosPermission.catalogsModels,
    descuentosEmpleadosDescuentosPermission.tabsModels,
    false,
  );

  const fetchData = async () => {
    setLoading(true);
    const _data = await getDescuentosEmpleadosDescuentos({ descuento_empleado: descuentoEmpleado });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _descuentos,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getConfiguracionDescuentos({ estados_globales: ESTATUS_AUTORIZADO }),
        getDescuentosEmpleadosDescuentos({ descuento_empleado: descuentoEmpleado }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setDescuentos(_descuentos);
        if (descuentoEmpleado) {
          setData(_data);
          setDisableAdd(false);
        } else {
          setData([]);
          setDisableAdd(true);
        }
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, [descuentoEmpleado]);

  const onCancel = () => {
    setVisible(false);
    setVisibleAlert(false);
    form.resetFields();
    setSelectedRowKeys([]);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const tarifaTipoNominaEmpleado = await getDescuentoEmpleadoDescuento(match.id);
    setTimeout(() => {
      if (tarifaTipoNominaEmpleado) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(tarifaTipoNominaEmpleado));
          setDescuentosFiltered(descuentos.filter((p) => !data.some((d) => {
            if (p?.id === form.getFieldValue('descuento')) {
              return false;
            }
            if (p?.id === d?.descuento) {
              return true;
            }
            return false;
          })));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        descuento_empleado: descuentoEmpleado,
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchDescuentoEmpleadoDescuento(key, values) : postDescuentoEmpleadoDescuento(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
      );
    }
  };

  const deleteItem = async () => {
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteDescuentoEmpleadoDescuento(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
  };

  const contentDelete = () => {
    const descuento = descuentos.find((desc) => desc?.id === form.getFieldValue('descuento'));
    return `Descuento ${descuento?.clave} - ${descuento?.concepto}`;
  };

  const onClickAdd = () => {
    onCancel();
    setDescuentosFiltered(descuentos.filter((p) => !data.some((d) => p?.id === d?.descuento)));
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  const columns = [
    {
      titleText: 'Descuento',
      dataIndex: 'descuento',
      key: 'descuento',
      width: 300,
      render: (descuento_id) => {
        const descuento_empleado_descuento = descuentos?.find((desc) => desc?.id === descuento_id);
        return `${descuento_empleado_descuento?.clave} - ${descuento_empleado_descuento?.concepto}`;
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (estado_id) => estados?.find((estado) => estado?.id === estado_id)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          data={data}
          cols={columns}
          allowExportParam={false}
          allowSearch={false}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          disableAdd={descuentoEmpleadoAutorizadoInactivo
          || disableAdd
          || isBlocked}
          disableDelete={descuentoEmpleadoAutorizadoInactivo
          || isBlocked}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          loading={loading}
          permission={getPermissionNominas}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Descuento`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="45%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                allowClear
                name="descuento"
                rules={rules.required}
                label="Descuento"
                hasFeedback
              >
                <Select
                  dataSource={descuentosFiltered}
                  render={(val) => `${val?.clave} - ${val?.concepto}`}
                  allowClear
                  disabled={isBlocked || descuentoEmpleadoAutorizadoInactivo}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.required}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0 || isBlocked || descuentoEmpleadoAutorizadoInactivo}
                />
              </Form.Item>
            </Col>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={contentDelete()}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default DescuentosEmpleadosDescuentos;
